import { defineStore } from 'pinia';
import axios from 'axios';
import getWelcomeModalConfig from '@/config/welcomeModalGuids';
import { fetchAssetFromDE } from '@/utils/fetches';
import { getAuth0Instance } from '@/auth';
import { getInstance as getDeAuthInstance } from '@/auth/deAuth';
import getToken from '@/utils/getToken';
import { useAppStore } from '@/stores/useAppStore';
import { ACCESS_STATES } from '@/utils/auth/accessState';
import { reportSilentError, reportNonFatalError } from '@/utils/storeErrorReporting';
import { getWelcomeModalSlidesError } from '@/utils/errorMessages';

const API_DOMAIN = import.meta.env.VITE_API_URL;

// Cached slide payloads embed DE `media_files` URLs, which go stale when an asset is
// re-encoded or replaced. Bound the cache so a stale URL self-corrects instead of
// leaving a user with a permanently broken video. Bump CACHE_VERSION to force a flush.
const CACHE_VERSION = 2;
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;

function cacheKeyFor(userRole) {
    return `welcomeModalSlides_${userRole}`;
}

// Role-specific modal IDs used to track dismissal per user on the backend
const WELCOME_MODAL_IDS = {
    educator: 'welcome-modal-educator',
    employee: 'welcome-modal-employee',
};

function getModalId(mode) {
    return WELCOME_MODAL_IDS[mode] || WELCOME_MODAL_IDS.educator;
}

async function getAuthToken() {
    const auth0Instance = getAuth0Instance();
    const deInstance = getDeAuthInstance();

    if (auth0Instance && auth0Instance.isAuthenticated) {
        return getToken(auth0Instance);
    }

    if (deInstance && deInstance.isAuthenticated) {
        return deInstance.getTokenSilently();
    }

    return null;
}

const useWelcomeModalStore = defineStore('welcomeModal', {
    state: () => ({
        show: false,
        loading: false,
        slides: null,
        retriedStaleSlides: false,
    }),

    actions: {
        async checkAndOpenIfNeeded() {
            const auth0Instance = getAuth0Instance();
            const deInstance = getDeAuthInstance();
            const appStore = useAppStore();

            const auth0Authenticated = auth0Instance && !auth0Instance.loading && auth0Instance.isAuthenticated;
            const deAuthenticated = deInstance && !deInstance.loading && deInstance.isAuthenticated;

            if (!auth0Authenticated && !deAuthenticated) {
                return;
            }

            if (appStore.accessState !== ACCESS_STATES.READY) {
                return;
            }

            if (appStore.mode === 'organization-admin') {
                return;
            }

            const config = getWelcomeModalConfig(appStore.mode);
            if (!config || !config.slides) {
                return;
            }

            const token = await getAuthToken();
            if (!token) {
                return;
            }

            try {
                const response = await axios.get(`${API_DOMAIN}/modal-dismissals`, {
                    headers: { Authorization: `Bearer ${token}` },
                });

                const dismissedIds = response.data?.dismissed_modal_ids || [];
                if (dismissedIds.includes(getModalId(appStore.mode))) {
                    return;
                }
            } catch (error) {
                reportSilentError(error);
                return;
            }

            this.openModal();
        },

        openModal() {
            this.show = true;
        },

        async closeModal() {
            this.show = false;

            const appStore = useAppStore();
            const token = await getAuthToken();

            if (!token) {
                return;
            }

            try {
                await axios.post(
                    `${API_DOMAIN}/modal-dismissals`,
                    { modal_id: getModalId(appStore.mode) },
                    { headers: { Authorization: `Bearer ${token}` } },
                );
            } catch (error) {
                reportSilentError(error);
            }
        },

        async loadSlides(userRole, forceRefresh = false) {
            this.loading = true;

            try {
                const config = getWelcomeModalConfig(userRole);

                if (!config || !config.slides) {
                    return null;
                }

                // Check cache first (unless force refresh is requested)
                const cacheKey = cacheKeyFor(userRole);
                const cachedData = !forceRefresh ? localStorage.getItem(cacheKey) : null;

                if (cachedData) {
                    try {
                        const parsed = JSON.parse(cachedData);
                        const fresh = parsed?.version === CACHE_VERSION && Date.now() - parsed.cachedAt < CACHE_TTL_MS;

                        if (fresh && Array.isArray(parsed.slides) && parsed.slides.length > 0) {
                            this.slides = parsed.slides;
                            return { slides: this.slides };
                        }

                        // Expired, wrong version, or a legacy bare-array entry — refetch.
                        localStorage.removeItem(cacheKey);
                    } catch (error) {
                        reportSilentError(error);
                        localStorage.removeItem(cacheKey);
                    }
                }

                const token = await getAuthToken();

                if (!token) {
                    throw new Error();
                }

                // Fetch video data for each slide
                const slidesWithData = await Promise.all(
                    config.slides.map(async (slide) => {
                        try {
                            const response = await fetchAssetFromDE(token, slide.videoGuid);

                            if (response.data?.meta && !response.data.meta.valid) {
                                const errorMsg = response.data.meta.message || 'Asset unavailable';
                                return { ...slide, data: null, error: errorMsg };
                            }

                            return { ...slide, data: response.data };
                        } catch (error) {
                            reportSilentError(error);
                            return { ...slide, data: null, error: 'Failed to load video data' };
                        }
                    }),
                );

                const allFailed = slidesWithData.every((slide) => slide.error);
                if (allFailed) {
                    throw new Error();
                }

                this.slides = slidesWithData;

                // Cache the slide data
                try {
                    localStorage.setItem(cacheKey, JSON.stringify({ version: CACHE_VERSION, cachedAt: Date.now(), slides: slidesWithData }));
                } catch (error) {
                    reportSilentError(error);
                }

                return { slides: this.slides };
            } catch (error) {
                reportNonFatalError(error, getWelcomeModalSlidesError());
                return null;
            } finally {
                this.loading = false;
            }
        },

        clearSlides() {
            this.slides = null;
            this.retriedStaleSlides = false;
        },

        /**
         * Drops the cached payload and refetches from DE.
         * Called when a video fails to load, which usually means a cached
         * `media_files` URL has gone stale. Retries once per modal session so a
         * genuinely broken asset can't cause a refetch loop.
         *
         * @param {string} userRole - Role whose slides should be refetched
         * @returns {boolean} True if a refetch was attempted
         */
        async retryStaleSlides(userRole) {
            if (this.retriedStaleSlides) {
                return false;
            }

            this.retriedStaleSlides = true;
            localStorage.removeItem(cacheKeyFor(userRole));
            await this.loadSlides(userRole, true);
            return true;
        },
    },
});

export default useWelcomeModalStore;
